"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import type {
  CopilotMessage,
  CopilotSettings,
  CopilotTask,
  CopilotTaskList,
  CopilotTaskStatus,
  CopilotToolCall,
  CopilotUsage,
  ProviderChatMessage,
} from "./types";
import { DEFAULT_SETTINGS, loadSettings, saveSettings } from "./storage";
import { getProvider } from "./providers";
import { buildOrkestriaSystemPrompt } from "./systemPrompt";
import { findSkill, getToolDefsForQuery } from "./skills";
import type { SkillContext } from "./skills";
import { useFollowInternal } from "./follow/FollowProvider";
import { useConfirmationInternal } from "./confirmation/ConfirmationProvider";
import { apiWithAuth } from "@/lib/axios";

const MAX_TOOL_ITERATIONS = 8;
/** Auto-continue: si tras MAX_TOOL_ITERATIONS no hay respuesta final, el sistema
 *  inyecta automáticamente "continúa" hasta MAX_AUTO_CONTINUES veces antes de
 *  preguntarle al user. */
const MAX_AUTO_CONTINUES = 2; // 3 batches totales = 24 iteraciones potenciales
/**
 * Si el LLM hace MÁS de N tool calls en un solo turn, desactivamos follow
 * automáticamente para no fatigar al user con narrativa visual eterna.
 * Las acciones siguen pasando, solo sin cursor + ripples + narración.
 */
const AUTO_DISABLE_FOLLOW_AT = 3;
/** Truncación de tool results — bajo para no inflar el contexto upstream. */
const TOOL_RESULT_MAX_CHARS = 800;
/** Si el turn acumula muchos mensajes (assistant + tool results), comprimimos
 *  los más viejos para evitar overflow de contexto upstream. */
const MAX_TURN_HISTORY = 10;
/** Cuántos mensajes recientes preservar siempre durante la compresión. */
const KEEP_RECENT_MESSAGES = 8;

/** Skills cuyos resultados son idempotentes — se pueden cachear durante un turn
 *  para evitar duplicar llamadas que el LLM hace por costumbre. */
const CACHEABLE_READ_SKILLS = new Set([
  "listMenu",
  "listCategories",
  "listMyRestaurants",
  "listTables",
  "listClients",
  "listMembers",
  "listInvitations",
  "listAlerts",
  "listAdditions",
  "listBankTransfers",
  "listCreditAccounts",
  "listCashRegisters",
  "listCashRegistersWithOpenSession",
  "listDayPromotions",
  "searchProduct",
  "getRestaurantInfo",
  "getRestaurantPreferences",
  "getActiveOrders",
  "getReservations",
  "getAnalytics",
]);

/** Skills que invalidan TODO el cache de lectura cuando se ejecutan exitosamente. */
const MUTATION_SKILL_NAMES = new Set([
  "createProduct",
  "updateProduct",
  "updateProductPrice",
  "archiveProduct",
  "restoreProduct",
  "createCategory",
  "updateCategory",
  "deleteCategory",
  "reorderProducts",
  "reorderCategories",
  "createTable",
  "updateTable",
  "deleteTable",
  "createClient",
  "updateClient",
  "deleteClient",
  "createReservation",
  "updateReservation",
  "deleteReservation",
  "createDineInOrder",
  "createDeliveryOrder",
  "updateOrderItem",
  "removeOrderItem",
  "closeOrder",
  "closeAllForTable",
  "openCashSession",
  "closeCashSession",
  "createCashRegister",
  "updateCashRegister",
  "archiveCashRegister",
  "restoreCashRegister",
  "toggleCashRegisterActive",
  "inviteMember",
  "updateMemberRole",
  "removeMember",
  "cancelInvitation",
  "markAlertRead",
  "markAllAlertsRead",
  "createBankTransfer",
  "updateBankTransfer",
  "deleteBankTransfer",
  "payCreditAccount",
  "createAddition",
  "updateAddition",
  "archiveAddition",
  "restoreAddition",
  "setProductAdditions",
  "setProductStock",
  "setProductSchedule",
  "setDayPromotion",
  "removeDayPromotion",
  "updateRestaurant",
  "updateRestaurantPreferences",
  "toggleRestaurantActive",
]);

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

/**
 * Valida ObjectIDs en los argumentos de un tool call ANTES de invocar la skill.
 * Si el LLM alucinó un ID (ej. truncado o inventado), devuelve un error claro
 * para que el LLM se corrija solo en la siguiente iteración.
 *
 * Reglas:
 *   - Cualquier campo cuyo nombre termine en "Id" debe ser un string de 24 hex.
 *   - "orderedIds", "additionIds" → arrays de strings: cada elemento debe ser válido.
 *   - "tabId", "categoryId" cuando es null → permitido (significa "sin categoría").
 */
/**
 * Comprime el turnHistory cuando crece demasiado. Estrategia:
 * - Conserva los primeros 2 mensajes (system context + primer user msg)
 * - Conserva los últimos KEEP_RECENT_MESSAGES
 * - Reemplaza el medio por un único mensaje resumen
 *
 * Esto evita que el modelo upstream reciba contextos enormes que disparan
 * 500s o timeouts en sesiones con muchas iteraciones.
 */
function compressTurnHistory(
  history: ProviderChatMessage[],
): ProviderChatMessage[] {
  if (history.length <= MAX_TURN_HISTORY) return history;

  const head = history.slice(0, 2);
  const tail = history.slice(-KEEP_RECENT_MESSAGES);
  const middleCount = history.length - head.length - tail.length;

  // Resumen sintético — el modelo entiende que se omitieron N pasos previos
  const summary: ProviderChatMessage = {
    role: "system",
    content: `[Historia comprimida: se omitieron ${middleCount} mensajes intermedios para no exceder el contexto. La tarea sigue en curso — usa los resultados recientes y el primer mensaje del usuario para continuar.]`,
  };

  return [...head, summary, ...tail];
}

function validateToolArgIds(
  args: Record<string, unknown>,
): { ok: false; field: string; value: unknown } | null {
  for (const [k, v] of Object.entries(args)) {
    if (v === null || v === undefined) continue;
    // Excepciones conocidas: tabId no es ObjectID (es string como "productos")
    if (k === "tabId" || k === "day" || k === "action") continue;

    if (k.endsWith("Id") && typeof v === "string") {
      if (!OBJECT_ID_RE.test(v)) {
        return { ok: false, field: k, value: v };
      }
    }
    if (k.endsWith("Ids") && Array.isArray(v)) {
      for (const item of v) {
        if (typeof item === "string" && !OBJECT_ID_RE.test(item)) {
          return { ok: false, field: k, value: item };
        }
      }
    }
  }
  return null;
}

/**
 * Labels amigables para narrar visualmente cada skill cuando se ejecuta.
 * Si la skill no aparece aquí, usa su id literal.
 */
const SKILL_FRIENDLY_LABELS: Record<string, string> = {
  navigateTab: "Navegando",
  listMyRestaurants: "Listando restaurantes",
  getAnalytics: "Consultando analítica",
  searchProduct: "Buscando producto",
  listCategories: "Listando categorías",
  getActiveOrders: "Consultando pedidos",
  getReservations: "Consultando reservas",
  createProduct: "Creando producto",
  updateProduct: "Actualizando producto",
  updateProductPrice: "Cambiando precio",
  archiveProduct: "Archivando producto",
  restoreProduct: "Restaurando producto",
  setProductStock: "Ajustando stock",
  setProductSchedule: "Configurando horario",
  setDayPromotion: "Configurando promoción",
  removeDayPromotion: "Quitando promoción",
  listDayPromotions: "Consultando promociones",
  createCategory: "Creando categoría",
  updateCategory: "Actualizando categoría",
  reorderProducts: "Reordenando productos",
  reorderCategories: "Reordenando categorías",
  listAdditions: "Listando adiciones",
  createAddition: "Creando adición",
  updateAddition: "Actualizando adición",
  archiveAddition: "Archivando adición",
  restoreAddition: "Restaurando adición",
  setProductAdditions: "Vinculando adiciones",
  listTables: "Listando mesas",
  createTable: "Creando mesa",
  updateTable: "Actualizando mesa",
  deleteTable: "Eliminando mesa",
  createDineInOrder: "Creando pedido",
  createDeliveryOrder: "Creando domicilio",
  updateOrderItem: "Modificando ítem",
  removeOrderItem: "Quitando ítem",
  closeOrder: "Cerrando pedido",
  closeAllForTable: "Cobrando mesa",
  sendInvoice: "Enviando factura",
  createReservation: "Creando reserva",
  updateReservation: "Actualizando reserva",
  deleteReservation: "Cancelando reserva",
  listClients: "Listando clientes",
  createClient: "Registrando cliente",
  updateClient: "Actualizando cliente",
  deleteClient: "Eliminando cliente",
  listCashRegisters: "Listando cajas",
  listCashRegistersWithOpenSession: "Verificando cajas activas",
  createCashRegister: "Creando caja",
  updateCashRegister: "Actualizando caja",
  archiveCashRegister: "Archivando caja",
  restoreCashRegister: "Restaurando caja",
  toggleCashRegisterActive: "Cambiando estado de caja",
  openCashSession: "Abriendo caja del día",
  closeCashSession: "Cerrando caja del día",
  listBankTransfers: "Listando intermediarios",
  createBankTransfer: "Creando intermediario",
  updateBankTransfer: "Actualizando intermediario",
  deleteBankTransfer: "Eliminando intermediario",
  listCreditAccounts: "Listando cuentas de crédito",
  payCreditAccount: "Registrando abono",
  listMembers: "Listando miembros",
  inviteMember: "Enviando invitación",
  updateMemberRole: "Cambiando rol",
  removeMember: "Eliminando miembro",
  listInvitations: "Listando invitaciones",
  cancelInvitation: "Cancelando invitación",
  listAlerts: "Listando alertas",
  markAlertRead: "Marcando alerta",
  markAllAlertsRead: "Marcando todas",
  getRestaurantInfo: "Consultando datos",
  updateRestaurant: "Actualizando restaurante",
  getRestaurantPreferences: "Consultando preferencias",
  updateRestaurantPreferences: "Actualizando preferencias",
  toggleRestaurantActive: "Cambiando estado",
  guideUser: "Guiando paso a paso",
  setTaskList: "Planificando",
  markTask: "Actualizando plan",
  bulkCreateProducts: "Creando productos en bloque",
  bulkCreateCategories: "Creando categorías en bloque",
  bulkDeleteCategories: "Eliminando categorías en bloque",
};

type CopilotContextValue = {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggleOpen: () => void;

  settings: CopilotSettings;
  settingsHydrated: boolean;
  updateSettings: (patch: Partial<CopilotSettings>) => void;

  messages: CopilotMessage[];
  isSending: boolean;
  isLoadingHistory: boolean;
  usage: CopilotUsage | null;
  taskList: CopilotTaskList | null;
  clearTaskList: () => void;
  send: (text: string) => Promise<void>;
  cancel: () => void;
  clear: () => void;
};

const CopilotContext = createContext<CopilotContextValue | null>(null);

let messageIdCounter = 0;
function makeId() {
  messageIdCounter += 1;
  return `m-${Date.now()}-${messageIdCounter}`;
}

type ServerMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  isError: boolean;
  createdAt: string;
};

export function CopilotProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const router = useRouter();
  const follow = useFollowInternal();
  const confirmation = useConfirmationInternal();
  const accessToken = (session as { accessToken?: string } | null | undefined)?.accessToken;

  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<CopilotSettings>(DEFAULT_SETTINGS);
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [usage, setUsage] = useState<CopilotUsage | null>(null);
  const [taskList, setTaskList] = useState<CopilotTaskList | null>(null);
  const taskListRef = useRef<CopilotTaskList | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const loadedForUserRef = useRef<string | null>(null);

  // Load settings (localStorage) on mount
  useEffect(() => {
    setSettings(loadSettings());
    setSettingsHydrated(true);
  }, []);

  // Sincronizar enabled del FollowController con settings.mode
  useEffect(() => {
    follow.setEnabled(settings.mode === "follow");
  }, [settings.mode, follow]);

  // Sincronizar permiso del Confirmation controller
  useEffect(() => {
    confirmation.setPermission(settings.permission ?? "ask");
  }, [settings.permission, confirmation]);

  // Mantener ref + window expuestos sincronizados con el state
  useEffect(() => {
    taskListRef.current = taskList;
    if (typeof window !== "undefined") {
      (window as any).__orkestriaTaskList = taskList;
    }
  }, [taskList]);

  // Listener global de eventos de task list (emitidos por skills setTaskList/markTask)
  useEffect(() => {
    const onSet = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        title?: string;
        tasks: CopilotTask[];
      };
      setTaskList({
        title: detail.title,
        tasks: detail.tasks,
        startedAt: Date.now(),
      });
    };
    const onMark = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        id: string;
        status: CopilotTaskStatus;
        note?: string;
      };
      setTaskList((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map((t) =>
            t.id === detail.id
              ? { ...t, status: detail.status, note: detail.note ?? t.note }
              : t,
          ),
        };
      });
    };
    const onClear = () => setTaskList(null);
    window.addEventListener("orkestria:tasklist:set", onSet);
    window.addEventListener("orkestria:tasklist:mark", onMark);
    window.addEventListener("orkestria:tasklist:clear", onClear);
    return () => {
      window.removeEventListener("orkestria:tasklist:set", onSet);
      window.removeEventListener("orkestria:tasklist:mark", onMark);
      window.removeEventListener("orkestria:tasklist:clear", onClear);
    };
  }, []);

  const clearTaskList = useCallback(() => setTaskList(null), []);

  // Load message history from DB when user becomes available
  useEffect(() => {
    if (!accessToken) return;
    const userKey = session?.user?.email ?? "anon";
    if (loadedForUserRef.current === userKey) return;
    loadedForUserRef.current = userKey;

    setIsLoadingHistory(true);
    apiWithAuth
      .get(`/me/copilot/messages?limit=100`, accessToken)
      .then(({ data }: { data: ServerMessage[] }) => {
        setMessages(
          data.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            createdAt: new Date(m.createdAt).getTime(),
            error: m.isError ? "(mensaje con error)" : undefined,
          })),
        );
      })
      .catch(() => undefined)
      .finally(() => setIsLoadingHistory(false));

    // Fetch usage en paralelo (con restaurantId si estamos en un restaurante)
    const restaurantMatch = typeof window !== "undefined"
      ? window.location.pathname.match(/^\/dashboard\/restaurantes\/([a-f0-9]{24})/i)
      : null;
    const initialRestaurantId = restaurantMatch?.[1];
    const usageUrl = initialRestaurantId
      ? `/me/copilot/usage?restaurantId=${initialRestaurantId}`
      : `/me/copilot/usage`;
    apiWithAuth
      .get(usageUrl, accessToken)
      .then(({ data }: { data: CopilotUsage }) => setUsage(data))
      .catch(() => undefined);
  }, [accessToken, session?.user?.email]);

  const updateSettings = useCallback((patch: Partial<CopilotSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  const toggleOpen = useCallback(() => setOpen((v) => !v), []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsSending(false);
  }, []);

  const persistMessage = useCallback(
    async (
      role: "user" | "assistant",
      content: string,
      isError = false,
    ): Promise<string | null> => {
      if (!accessToken) return null;
      try {
        const { data } = await apiWithAuth.post(
          `/me/copilot/messages`,
          { role, content, isError },
          accessToken,
        );
        return (data as ServerMessage)?.id ?? null;
      } catch {
        return null;
      }
    },
    [accessToken],
  );

  const clear = useCallback(() => {
    setMessages([]);
    if (accessToken) {
      apiWithAuth.delete(`/me/copilot/messages`, accessToken).catch(() => undefined);
    }
  }, [accessToken]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      const userMsg: CopilotMessage = {
        id: makeId(),
        role: "user",
        content: trimmed,
        createdAt: Date.now(),
      };
      const assistantMsg: CopilotMessage = {
        id: makeId(),
        role: "assistant",
        content: "",
        createdAt: Date.now(),
        pending: true,
        toolCalls: [],
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsSending(true);

      // Persistencia user msg
      void persistMessage("user", trimmed, false).then((serverId) => {
        if (serverId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === userMsg.id ? { ...m, id: serverId } : m)),
          );
        }
      });

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const userWantsFollow = settings.mode === "follow";
      // effectiveFollowEnabled puede mutar durante el turn si la tarea crece
      let effectiveFollowEnabled = userWantsFollow;
      let toolCallsInTurn = 0;

      const buildSkillCtx = (followOn: boolean): SkillContext => ({
        router: {
          // Avisar al FollowProvider que es una navegación INICIADA por la skill
          // — sin esto, el cambio de ruta abortaría el follow en curso.
          push: (url: string) => {
            if (followOn) follow.expectNavigation();
            router.push(url);
          },
          replace: (url: string) => {
            if (followOn) follow.expectNavigation();
            router.replace(url);
          },
        },
        accessToken,
        follow: {
          enabled: followOn,
          step: follow.methods.step,
          pointAt: follow.methods.pointAt,
          highlight: follow.methods.highlight,
          clickAt: follow.methods.clickAt,
          realClickAt: follow.methods.realClickAt,
          clear: follow.methods.clear,
        },
        confirmation: confirmation.controller,
      });

      // Construir system prompt con contexto dinámico (URL + task list).
      // OJO: lo construimos como función para que cada iteración del while
      // use el ESTADO MÁS RECIENTE del task list (memoria del agente).
      const buildPrompt = () => {
        try {
          const url = new URL(window.location.href);
          // Leemos taskList directo del state actual via ref/closure-fresh
          return buildOrkestriaSystemPrompt({
            pathname: url.pathname,
            searchParams: url.searchParams,
            taskList: taskListRef.current,
            followEnabled: effectiveFollowEnabled,
          });
        } catch {
          return buildOrkestriaSystemPrompt({
            taskList: taskListRef.current,
            followEnabled: effectiveFollowEnabled,
          });
        }
      };

      try {
        const provider = getProvider(settings.providerId);

        // Reconstruir conversación para el LLM (solo user + assistant text;
        // tool calls de turnos pasados no se reenvían — son efímeros).
        const baseHistory: ProviderChatMessage[] = [...messages, userMsg]
          .filter((m) => m.role === "user" || m.role === "assistant")
          .filter((m) => !m.pending)
          .map((m) => ({ role: m.role, content: m.content }));

        // Tool defs filtrados por keywords del último mensaje del user.
        // Reduce ~50-70% el tamaño del request upstream cuando el dominio
        // está claro (ej. "crea un producto" → solo skills products/categories).
        const tools = getToolDefsForQuery(trimmed);
        let iter = 0;
        let finalContent = "";
        let autoContinuesUsed = 0;
        let continueAutoLoop = true;
        const turnHistory: ProviderChatMessage[] = [...baseHistory];
        // Generar turnId
        const turnId = `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        // Cache de resultados de skills idempotentes durante este turn
        const skillCache = new Map<
          string,
          { ok: true; result: unknown; humanSummary?: string }
        >();
        // Contador de cuántas veces el LLM ha intentado llamar cada skill+args
        // idénticos. Tras N repeticiones devolvemos un STOP duro para
        // romper bucles obstinados.
        const skillCallCount = new Map<string, number>();
        const MAX_REPEATED_CALLS = 3;

        // Outer loop: re-entrar al inner cuando hay auto-continue
        outerLoop: while (continueAutoLoop) {
          continueAutoLoop = false;
          iter = 0;

        while (iter < MAX_TOOL_ITERATIONS) {
          iter += 1;

          // Para el provider 'orkestria' la "apiKey" es el JWT del usuario
          // (passthrough al backend). Para los demás, es la key del usuario
          // configurada en Settings.
          const isOrkestria = settings.providerId === "orkestria";
          // Comprimir history si crece demasiado (evita 500s upstream)
          const compactHistory = compressTurnHistory(turnHistory);

          // Re-build prompt cada iteración con el taskList más reciente
          const freshSystemPrompt = buildPrompt();

          // Extraer restaurantId de la URL para cuotas de restaurante
          let currentRestaurantId: string | undefined;
          try {
            const m = window.location.pathname.match(
              /^\/dashboard\/restaurantes\/([a-f0-9]{24})/i,
            );
            currentRestaurantId = m?.[1];
          } catch {
            currentRestaurantId = undefined;
          }

          const response = await provider.chat({
            baseUrl: settings.baseUrl || provider.defaultBaseUrl,
            apiKey: isOrkestria
              ? accessToken
              : settings.apiKey || undefined,
            model: settings.model || provider.defaultModel,
            systemPrompt: freshSystemPrompt,
            messages: compactHistory,
            tools,
            signal: ctrl.signal,
            turnId, // dedupe usage event
            restaurantId: currentRestaurantId, // cuotas de restaurante
          });

          // Si el provider devolvió usage en meta, actualizamos
          if (response.meta?.usage) {
            setUsage(response.meta.usage);
          }

          // Append the assistant turn to history (with tool_calls if any)
          const assistantTurn: ProviderChatMessage = {
            role: "assistant",
            content: response.content,
          };
          if (response.toolCalls.length > 0) {
            assistantTurn.tool_calls = response.toolCalls.map((tc) => ({
              id: tc.id,
              function: { name: tc.name, arguments: tc.arguments },
            }));
          }
          turnHistory.push(assistantTurn);

          // Si no hay tool calls → respuesta final
          if (response.toolCalls.length === 0) {
            finalContent = response.content;
            break;
          }

          toolCallsInTurn += response.toolCalls.length;

          // Auto-desactivar follow si la tarea está creciendo demasiado
          if (
            userWantsFollow &&
            effectiveFollowEnabled &&
            toolCallsInTurn > AUTO_DISABLE_FOLLOW_AT
          ) {
            effectiveFollowEnabled = false;
            follow.setEnabled(false);
            follow.methods.clear();
            // Aviso al user dentro del propio mensaje del asistente
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? {
                      ...m,
                      content:
                        (m.content ? m.content + "\n\n" : "") +
                        "_📍 Modo guía visual pausado: tarea grande detectada. Continúo sin animaciones para ir más rápido._",
                    }
                  : m,
              ),
            );
          }

          // Dedup DENTRO del batch — el LLM a veces emite el mismo
          // tool_call (idempotente) varias veces en paralelo en una sola
          // respuesta. Identificamos un "ganador" por cacheKey y a los demás
          // les damos status "duplicate": NO se renderizan en el UI y NO se
          // ejecuta la skill, pero SÍ enviamos un tool result al LLM
          // (requerido por la API: cada tool_call_id debe tener respuesta).
          // Mutations NO se deduplican (podría ser intencional).
          const winnerByKey = new Map<string, string>(); // cacheKey → tc.id ganador
          const duplicateIds = new Set<string>();
          for (const tc of response.toolCalls) {
            if (!CACHEABLE_READ_SKILLS.has(tc.name)) continue;
            const key = `${tc.name}:${JSON.stringify(tc.arguments)}`;
            if (winnerByKey.has(key)) {
              duplicateIds.add(tc.id);
            } else {
              winnerByKey.set(key, tc.id);
            }
          }

          // Renderizar tool calls como running — SALTAMOS los duplicados
          // (no aparecen en el UI para no confundir al usuario).
          const newToolCalls: CopilotToolCall[] = response.toolCalls
            .filter((tc) => !duplicateIds.has(tc.id))
            .map((tc) => ({
              id: tc.id,
              name: tc.name,
              arguments: tc.arguments,
              status: "running",
              startedAt: Date.now(),
            }));

          // Responder a los duplicados en turnHistory SIN renderizar UI ni
          // ejecutar nada. La API requiere una respuesta para cada
          // tool_call_id; le indicamos al LLM que fue una llamada redundante.
          for (const tc of response.toolCalls) {
            if (!duplicateIds.has(tc.id)) continue;
            turnHistory.push({
              role: "tool",
              content: JSON.stringify({
                deduped: true,
                note: `Llamada paralela duplicada (mismos args que otro tool_call en este turno). NO emitas tool_calls duplicados — el ganador ya devolvió el resultado. Continúa con el plan.`,
              }),
              tool_call_id: tc.id,
              name: tc.name,
            });
          }
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id
                ? {
                    ...m,
                    content: response.content,
                    toolCalls: [...(m.toolCalls ?? []), ...newToolCalls],
                  }
                : m,
            ),
          );

          // Ejecutar cada tool call y agregar resultado a history
          for (const tc of newToolCalls) {
            const skill = findSkill(tc.name);
            let res:
              | { ok: true; result: unknown; humanSummary?: string }
              | { ok: false; error: string };
            if (!skill) {
              res = { ok: false, error: `Skill desconocida: ${tc.name}` };
            } else {
              // Narrativa default: si el modo follow está activo y la skill
              // no inicia su propia narrativa rápido, mostramos un step
              // genérico para que el user vea actividad.
              if (effectiveFollowEnabled) {
                const friendly = SKILL_FRIENDLY_LABELS[tc.name] ?? tc.name;
                // Fire-and-forget — la skill puede sobrescribir con su narrativa
                void follow.methods.step(`⚙️ ${friendly}…`, {
                  durationMs: 300,
                });
              }
              // Auto-inyección: si el LLM no mandó restaurantId o lo mandó
              // malformado y estamos navegando dentro de un restaurante,
               // inyectamos el ID real del URL. Evita que el LLM tenga que
              // copiar un ObjectID de 24 chars (que tiende a alucinar/truncar).
              try {
                const urlMatch = window.location.pathname.match(
                  /^\/dashboard\/restaurantes\/([a-f0-9]{24})/i,
                );
                const urlRestaurantId = urlMatch?.[1];
                if (urlRestaurantId) {
                  const current = (tc.arguments as Record<string, unknown>)
                    .restaurantId;
                  const isValid =
                    typeof current === "string" && OBJECT_ID_RE.test(current);
                  if (!isValid) {
                    (tc.arguments as Record<string, unknown>).restaurantId =
                      urlRestaurantId;
                  }
                }
              } catch {
                // ignore
              }

              // Validación de ObjectIDs ANTES de tocar la skill
              const idValidation = validateToolArgIds(tc.arguments);
              if (idValidation) {
                res = {
                  ok: false,
                  error: `${idValidation.field} inválido: "${String(idValidation.value)}". Debe ser un ObjectID real (24 caracteres hex). NO inventes IDs — usa la skill de búsqueda apropiada (listMyRestaurants, searchProduct, listCategories, listClients, listTables, etc.) para obtener el ID correcto y vuelve a intentar.`,
                };
                // Update UI tool call inmediatamente
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsg.id
                      ? {
                          ...m,
                          toolCalls: (m.toolCalls ?? []).map((t) =>
                            t.id === tc.id
                              ? {
                                  ...t,
                                  status: "error",
                                  error: res.ok ? undefined : (res as any).error,
                                  endedAt: Date.now(),
                                }
                              : t,
                          ),
                        }
                      : m,
                  ),
                );
                // Append a history y continuar con el siguiente tool call
                turnHistory.push({
                  role: "tool",
                  content: JSON.stringify({
                    error: (res as { ok: false; error: string }).error,
                  }),
                  tool_call_id: tc.id,
                  name: tc.name,
                });
                continue;
              }

              // Cache hit para skills de lectura idempotentes
              const cacheKey = `${tc.name}:${JSON.stringify(tc.arguments)}`;
              if (
                CACHEABLE_READ_SKILLS.has(tc.name) &&
                skillCache.has(cacheKey)
              ) {
                const cached = skillCache.get(cacheKey)!;
                const repeatCount =
                  (skillCallCount.get(cacheKey) ?? 0) + 1;
                skillCallCount.set(cacheKey, repeatCount);

                res = {
                  ok: true,
                  result: cached.result,
                  humanSummary: `(cache) ${cached.humanSummary ?? ""}`,
                };
                // Saltamos la ejecución real (no se vuelve a fetchear)
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsg.id
                      ? {
                          ...m,
                          toolCalls: (m.toolCalls ?? []).map((t) =>
                            t.id === tc.id
                              ? {
                                  ...t,
                                  status: "success",
                                  result: cached.result,
                                  endedAt: Date.now(),
                                }
                              : t,
                          ),
                        }
                      : m,
                  ),
                );

                // Tras MAX_REPEATED_CALLS llamadas idénticas → STOP duro.
                // Devolvemos un error explícito en lugar del resultado, así
                // el LLM no se queda atascado pidiéndolo en bucle.
                if (repeatCount >= MAX_REPEATED_CALLS) {
                  turnHistory.push({
                    role: "tool",
                    content: JSON.stringify({
                      error: `STOP — has llamado ${tc.name} ${repeatCount} veces con los mismos argumentos en este turn. Ya tienes el resultado. Continúa con el siguiente paso del plan o responde al usuario con la información que ya obtuviste. NO vuelvas a llamar esta skill.`,
                    }),
                    tool_call_id: tc.id,
                    name: tc.name,
                  });
                  continue;
                }

                // Devolvemos el RESULTADO completo (no solo una nota) para
                // que el LLM, aunque haya olvidado el output anterior tras
                // compresión, tenga toda la data y pueda avanzar sin
                // necesidad de re-llamar.
                turnHistory.push({
                  role: "tool",
                  content: JSON.stringify({
                    cached: true,
                    note: `Ya obtuviste este resultado en este turn (llamada #${repeatCount}). NO repitas — usa esta data y avanza.`,
                    result: cached.result,
                  }),
                  tool_call_id: tc.id,
                  name: tc.name,
                });
                continue;
              }

              try {
                res = await skill.run(
                  tc.arguments,
                  buildSkillCtx(effectiveFollowEnabled),
                );

                // Cachear resultados exitosos de skills de lectura
                if (CACHEABLE_READ_SKILLS.has(tc.name) && res.ok) {
                  skillCache.set(cacheKey, {
                    ok: true,
                    result: res.result,
                    humanSummary: res.humanSummary,
                  });
                }
                // Invalidar cache cuando hay mutación exitosa
                if (MUTATION_SKILL_NAMES.has(tc.name) && res.ok) {
                  skillCache.clear();
                }

                // Si la skill es guideUser y follow está OFF, sugerimos
                // visualmente al usuario activarlo (banner inline en el msg).
                if (
                  tc.name === "guideUser" &&
                  res.ok &&
                  !effectiveFollowEnabled &&
                  settings.mode !== "follow"
                ) {
                  const tutorialTitle =
                    (res.result as any)?.title ?? "esta guía";
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMsg.id
                        ? {
                            ...m,
                            followSuggestion: {
                              title: tutorialTitle,
                              skillName: tc.name,
                              args: tc.arguments,
                            },
                          }
                        : m,
                    ),
                  );
                }
              } catch (err) {
                res = {
                  ok: false,
                  error: (err as Error)?.message ?? "Error ejecutando skill.",
                };
              }
            }

            // Update UI tool call
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? {
                      ...m,
                      toolCalls: (m.toolCalls ?? []).map((t) =>
                        t.id === tc.id
                          ? {
                              ...t,
                              status: res.ok ? "success" : "error",
                              result: res.ok ? res.result : undefined,
                              error: res.ok ? undefined : res.error,
                              endedAt: Date.now(),
                            }
                          : t,
                      ),
                    }
                  : m,
              ),
            );

            // Append tool result to history for next iteration.
            // Truncamos para no inflar el contexto upstream (causa 500s en
            // modelos cloud con context limits).
            const toolPayload = res.ok
              ? JSON.stringify(res.result).slice(0, TOOL_RESULT_MAX_CHARS)
              : JSON.stringify({ error: res.error });
            turnHistory.push({
              role: "tool",
              content: toolPayload,
              tool_call_id: tc.id,
              name: tc.name,
            });
          }

          // Loop: el LLM verá los resultados y decidirá si responde o llama más tools
        }

        // Auto-continue: si llegamos al límite sin respuesta final y aún no
        // agotamos los auto-continues, inyectamos "continúa" silenciosamente
        // (sin banner) para que parezca una respuesta continua. Cada auto-
        // continue SÍ cuenta como un evento de uso (no se deduplica).
        let needsContinuePrompt = false;
        if (iter >= MAX_TOOL_ITERATIONS && !finalContent) {
          if (autoContinuesUsed < MAX_AUTO_CONTINUES) {
            autoContinuesUsed += 1;
            turnHistory.push({
              role: "user",
              content:
                "Continúa con la tarea original. Sigue ejecutando las skills necesarias hasta completar lo solicitado. Si ya está completo, responde con un resumen final.",
            });
            continueAutoLoop = true;
          } else {
            needsContinuePrompt = true;
            finalContent =
              "Llegué al límite de pasos automáticos. La tarea aún no está completa. ¿Quieres que siga?";
          }
        }

        if (continueAutoLoop) {
          continue outerLoop;
        }

        // Setear mensaje final con (o sin) continuePrompt
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? {
                  ...m,
                  content: finalContent || m.content,
                  pending: false,
                  ...(needsContinuePrompt
                    ? { continuePrompt: { autoContinuesUsed } }
                    : {}),
                }
              : m,
          ),
        );
        break outerLoop;
        } // fin outerLoop while body

        if (finalContent) {
          void persistMessage("assistant", finalContent, false).then(
            (serverId) => {
              if (serverId) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsg.id ? { ...m, id: serverId } : m,
                  ),
                );
              }
            },
          );
        }
      } catch (err) {
        const aborted =
          (err as Error)?.name === "AbortError" || ctrl.signal.aborted;
        const errAny = err as Error & {
          isLimitError?: boolean;
          usage?: CopilotUsage;
        };
        const errorMsg = aborted
          ? "Cancelado."
          : errAny?.message ||
            "No se pudo conectar con el modelo. Intenta de nuevo.";
        // Si el error trae usage actualizado (caso 429), refrescamos UI
        if (errAny?.usage) {
          setUsage(errAny.usage);
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? {
                  ...m,
                  pending: false,
                  error: errorMsg,
                  content: aborted ? "" : m.content,
                }
              : m,
          ),
        );
      } finally {
        if (abortRef.current === ctrl) abortRef.current = null;
        setIsSending(false);
        // Safety: cualquier skill que abortó deja overlay limpio
        follow.methods.clear();
        // Restaurar follow al estado del setting si lo desactivamos durante el turn
        if (settings.mode === "follow") {
          follow.setEnabled(true);
        }
      }
    },
    [isSending, messages, settings, persistMessage, accessToken, router, follow, confirmation],
  );

  const value = useMemo<CopilotContextValue>(
    () => ({
      open,
      setOpen,
      toggleOpen,
      settings,
      settingsHydrated,
      updateSettings,
      messages,
      isSending,
      isLoadingHistory,
      usage,
      taskList,
      clearTaskList,
      send,
      cancel,
      clear,
    }),
    [
      open,
      toggleOpen,
      settings,
      settingsHydrated,
      updateSettings,
      messages,
      isSending,
      isLoadingHistory,
      usage,
      taskList,
      clearTaskList,
      send,
      cancel,
      clear,
    ],
  );

  return (
    <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>
  );
}

export function useCopilot() {
  const ctx = useContext(CopilotContext);
  if (!ctx) {
    throw new Error("useCopilot debe usarse dentro de CopilotProvider");
  }
  return ctx;
}
